import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';


interface UrlStateOptions {
  replace?: boolean;
  keepDefault?: boolean;
}

export function useUrlParam(
  key: string,
  defaultValue = '',
  opts: UrlStateOptions = {},
): [string, (val: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const replace = opts.replace ?? true;
  const keepDefault = opts.keepDefault ?? false;
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (val: string) => {
      const next = new URLSearchParams(window.location.search);
      if (val === '') next.delete(key);
      else if (val === defaultValue && !keepDefault) next.delete(key);
      else next.set(key, val);
      setSearchParams(next, { replace });
    },
    [key, defaultValue, replace, keepDefault, setSearchParams],
  );

  useEffect(() => {
    if (!keepDefault || !defaultValue) return;
    const next = new URLSearchParams(window.location.search);
    if (next.get(key) !== null) return;
    next.set(key, defaultValue);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [value, setValue];
}

export function useUrlTab(
  defaultValue: string,
  key = 'tab',
  opts: UrlStateOptions = {},
): [string, (val: string) => void] {
  return useUrlParam(key, defaultValue, {
    replace: opts.replace ?? false,
    keepDefault: opts.keepDefault,
  });
}

export function useUrlParams(): {
  params: URLSearchParams;
  get: (key: string, fallback?: string) => string;
  set: (values: Record<string, string | null | undefined>, opts?: UrlStateOptions) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback(
    (key: string, fallback = '') => searchParams.get(key) ?? fallback,
    [searchParams],
  );

  const set = useCallback(
    (values: Record<string, string | null | undefined>, opts: UrlStateOptions = {}) => {
      const nextParams = new URLSearchParams(window.location.search);
      for (const [k, v] of Object.entries(values)) {
        if (v === '' || v === null || v === undefined) nextParams.delete(k);
        else nextParams.set(k, v);
      }
      setSearchParams(nextParams, { replace: opts.replace ?? true });
    },
    [setSearchParams],
  );

  return { params: searchParams, get, set };
}


type SetScopedParams = (
  next: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
  opts?: { replace?: boolean },
) => void;

export function useScopedSearchParams(
  useLocal: boolean,
): [URLSearchParams, SetScopedParams] {
  const [routeParams, setRouteParams] = useSearchParams();
  const [localParams, setLocalParams] = useState<URLSearchParams>(
    () => new URLSearchParams(),
  );

  const setParams = useCallback<SetScopedParams>(
    (next, opts) => {
      if (useLocal) {
        setLocalParams((prev) =>
          new URLSearchParams(typeof next === 'function' ? next(prev) : next),
        );
      } else {
        setRouteParams(next, opts);
      }
    },
    [useLocal, setRouteParams],
  );

  return [useLocal ? localParams : routeParams, setParams];
}

export function useScopedUrlParam(
  useLocal: boolean,
  key: string,
  defaultValue = '',
  opts: UrlStateOptions = {},
): [string, (val: string) => void] {
  const [urlValue, setUrlValue] = useUrlParam(key, defaultValue, opts);
  const [localValue, setLocalValue] = useState(defaultValue);
  return useLocal ? [localValue, setLocalValue] : [urlValue, setUrlValue];
}

export function useScopedUrlTab(
  useLocal: boolean,
  defaultValue: string,
  key = 'tab',
  opts: UrlStateOptions = {},
): [string, (val: string) => void] {
  return useScopedUrlParam(useLocal, key, defaultValue, {
    replace: opts.replace ?? false,
    keepDefault: opts.keepDefault,
  });
}
